import { Link, graphql, withPrefix } from 'gatsby';
import { useState, Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Container } from '../components/container';
import { Columns, Column } from '../components/columns';
import { AnimatedText } from '../components/animated-text';
import Dropdown from 'react-dropdown';
import { Caret } from '../components/svgs/caret';
import { Vine } from '../components/vine';
import { useVisibleDelay } from '../hooks/use-delay';
import { Hero } from '../components/hero';
import { transitionSettings } from '../lib/constants';

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const ALL_VALUE = 999;

export default function FundingPartners({ data }) {
  const donors = data?.donors?.nodes.map((file) => file.childMarkdownRemark);

  const options = donors?.map((donor) => ({
    value: +donor?.frontmatter?.order,
    label: donor?.frontmatter?.title,
  }));

  options.push({ value: ALL_VALUE, label: 'View All' });

  const [ref, animateIntroSection] = useVisibleDelay();
  const [selectedOption, setSelectedOption] = useState(null);
  const [[prevIndex, direction], setDirection] = useState([-1, -1]);

  const donor = donors.find(
    (loan) => loan.frontmatter.order === selectedOption?.value
  );

  const handleSelect = (option) => {
    setDirection([option.value, option.value - prevIndex]);
    setSelectedOption(option);
  };

  return (
    <>
      <Hero />
      <article className="page narrow">
        <Container className="py0">
          <Columns>
            <Column className="one-third-ish">
              <h1 ref={ref} className="undervine">
                <span>
                  <AnimatedText isVisible={animateIntroSection}>
                    Funding Partners
                  </AnimatedText>
                  {animateIntroSection && <Vine />}
                </span>
              </h1>
            </Column>

            <Column className="two-thirds-ish">
              <p>
                In order to meet our mission and serve our region, the Northland
                Foundation relies on support from other foundations,
                governmental agencies, businesses, and individuals. We thank
                them for their partnership and generosity. None of this work
                happens without them.
              </p>
              <p>
                View individual donors and partner organizations using the menu,
                below. For funds received specifically for COVID-19 relief
                efforts, please visit the{' '}
                <Link to="/covid-response">COVID Response</Link> page. To see a
                more detailed breakdown of funds raised, see our{' '}
                <a
                  href={withPrefix(`/nof_funding_snapshot_2021.pdf`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  2020-2021 Funding Snapshot
                </a>
                .
              </p>
            </Column>
          </Columns>
        </Container>

        <Container className="funding-partners__dropdown-section">
          <div className="funding-partners__select dropdown-group">
            <label>View Funders and Donors</label>
            <p>
              Use the drop-down to view all those who provided funding support
              July 1, 2020 through June 30, 2021.
            </p>

            <div
              className="Dropdown"
              style={{
                minWidth: '60%',
              }}
            >
              <Dropdown
                value={selectedOption}
                onChange={handleSelect}
                options={options}
                placeholder={'Select'}
              />
              <Caret />
            </div>
          </div>

          <AnimatePresence initial={false} custom={direction}>
            {selectedOption?.value === ALL_VALUE ? (
              <motion.div
                className="funding-partners__detail"
                key={'all'}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ ...transitionSettings }}
              >
                <h3 className="undervine">
                  <span>
                    All Partners
                    <Vine />
                  </span>
                </h3>
                {donors.map((d) => (
                  <Fragment key={d.id}>
                    <h4>{d.frontmatter.title}</h4>
                    <div
                      className="funding-partners__detail-list"
                      dangerouslySetInnerHTML={{ __html: d.html }}
                    />
                  </Fragment>
                ))}
              </motion.div>
            ) : (
              donor && (
                <motion.div
                  className="funding-partners__detail"
                  key={donor.id}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ ...transitionSettings }}
                >
                  <h3 className="undervine">
                    <span>
                      {donor.frontmatter.title}
                      <Vine />
                    </span>
                  </h3>

                  <div
                    className="funding-partners__detail-list"
                    dangerouslySetInnerHTML={{ __html: donor.html }}
                  />
                </motion.div>
              )
            )}
          </AnimatePresence>
        </Container>
      </article>
    </>
  );
}

export const pageQuery = graphql`
  query {
    donors: allFile(
      filter: { sourceInstanceName: { eq: "donors" }, extension: { eq: "md" } }
      sort: { fields: childrenMarkdownRemark___frontmatter___order, order: ASC }
    ) {
      nodes {
        childMarkdownRemark {
          id
          html
          fields {
            slug
          }
          frontmatter {
            order
            title
          }
        }
      }
    }
  }
`;
